"use client";

import { useState, useEffect } from "react";
import {
  buildDaySummary,
  getDatesWithNotes,
  DaySummary,
  CategoryGroup,
} from "@/lib/summary";

interface DaySummaryPanelProps {
  onClose: () => void;
  refreshKey?: number;
}

export default function DaySummaryPanel({
  onClose,
  refreshKey = 0,
}: DaySummaryPanelProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [recentDates, setRecentDates] = useState<Date[]>([]);
  const [summary, setSummary] = useState<DaySummary | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );
  const [expandedOccasions, setExpandedOccasions] = useState<Set<string>>(
    new Set(),
  );

  useEffect(() => {
    const dates = getDatesWithNotes(7);
    // Always include today at the beginning, even if no notes exist
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split("T")[0];

    // Check if today is already in the list
    const hasTodayInList = dates.some(
      (d) => d.toISOString().split("T")[0] === todayStr,
    );

    if (!hasTodayInList) {
      dates.unshift(today);
    }

    setRecentDates(dates);
    // Default to today
    setSelectedDate(today);
  }, [refreshKey]);

  useEffect(() => {
    const daySummary = buildDaySummary(selectedDate);
    setSummary(daySummary);
    // Auto-expand all categories by default
    const allCategories = new Set(daySummary.categories.map((c) => c.category));
    setExpandedCategories(allCategories);
    // Auto-expand all occasions by default
    const allOccasions = new Set<string>();
    daySummary.categories.forEach((cat) => {
      cat.occasions.forEach((occ) => {
        allOccasions.add(`${cat.category}-${occ.occasion}`);
      });
    });
    setExpandedOccasions(allOccasions);
  }, [selectedDate, refreshKey]);
  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const toggleOccasion = (key: string) => {
    setExpandedOccasions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleDatePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = new Date(e.target.value);
    setSelectedDate(date);
    setShowDatePicker(false);
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-white to-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-blue to-brand-cyan p-4 shadow-md">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-white font-semibold text-lg">📊 Day Summary</h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>

        {/* Date selector */}
        <div className="flex gap-2 items-center">
          <select
            value={selectedDate.toISOString().split("T")[0]}
            onChange={(e) => setSelectedDate(new Date(e.target.value))}
            className="flex-1 px-3 py-2 text-sm rounded-lg bg-white/20 text-white border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
          >
            {recentDates.map((date) => (
              <option
                key={date.toISOString()}
                value={date.toISOString().split("T")[0]}
                className="text-gray-800"
              >
                {formatDateLabel(date)}
              </option>
            ))}
            {recentDates.length === 0 && (
              <option
                value={selectedDate.toISOString().split("T")[0]}
                className="text-gray-800"
              >
                No recent notes
              </option>
            )}
          </select>
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
            title="Pick a date"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </button>
        </div>

        {showDatePicker && (
          <div className="mt-2">
            <input
              type="date"
              onChange={handleDatePickerChange}
              className="w-full px-3 py-2 text-sm rounded-lg bg-white text-gray-800 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-blue"
            />
          </div>
        )}
      </div>

      {/* Summary content */}
      <div className="flex-1 overflow-y-auto p-4">
        {!summary || summary.totalNotes === 0 ? (
          <div className="text-center text-gray-500 mt-12">
            <div className="text-5xl mb-3">📭</div>
            <p className="font-medium">No notes for this day</p>
            <p className="text-sm mt-1">
              Select a different date or create new notes
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-gray-500 mb-2">
              {summary.totalNotes} note{summary.totalNotes !== 1 ? "s" : ""} on{" "}
              {summary.dateString}
            </div>

            {summary.categories.map((category) => (
              <CategorySection
                key={category.category}
                category={category}
                isExpanded={expandedCategories.has(category.category)}
                onToggle={() => toggleCategory(category.category)}
                expandedOccasions={expandedOccasions}
                onToggleOccasion={toggleOccasion}
                formatTime={formatTime}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface CategorySectionProps {
  category: CategoryGroup;
  isExpanded: boolean;
  onToggle: () => void;
  expandedOccasions: Set<string>;
  onToggleOccasion: (key: string) => void;
  formatTime: (dateString: string) => string;
}

function CategorySection({
  category,
  isExpanded,
  onToggle,
  expandedOccasions,
  onToggleOccasion,
  formatTime,
}: CategorySectionProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between bg-gradient-to-r from-brand-blue/5 to-brand-cyan/5 hover:from-brand-blue/10 hover:to-brand-cyan/10 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">📁</span>
          <span className="font-semibold text-gray-800">
            {category.category}
          </span>
          <span className="text-xs bg-brand-blue/10 text-brand-blue px-2 py-0.5 rounded-full">
            {category.occasions.reduce(
              (acc, o) =>
                acc + o.topics.reduce((a, t) => a + t.notes.length, 0),
              0,
            )}{" "}
            notes
          </span>
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-5 w-5 text-gray-500 transition-transform ${isExpanded ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isExpanded && (
        <div className="p-3 space-y-3">
          {category.occasions.map((occasion) => {
            const occasionKey = `${category.category}-${occasion.occasion}`;
            const isOccasionExpanded = expandedOccasions.has(occasionKey);

            return (
              <div
                key={occasion.occasion}
                className="border-l-2 border-brand-cyan/30 pl-3"
              >
                <button
                  onClick={() => onToggleOccasion(occasionKey)}
                  className="w-full flex items-center justify-between py-1 hover:bg-gray-50 rounded transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm">📌</span>
                    <span className="font-medium text-gray-700 text-sm">
                      {occasion.occasion}
                    </span>
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 text-gray-400 transition-transform ${isOccasionExpanded ? "rotate-180" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {isOccasionExpanded && (
                  <div className="mt-2 space-y-2 ml-2">
                    {occasion.topics.map((topic) => (
                      <div
                        key={topic.topic}
                        className="bg-notepad-yellow/50 rounded-lg p-3 border border-notepad-line/30"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs">📝</span>
                          <span className="font-medium text-gray-800 text-sm">
                            {topic.topic}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({topic.notes.length} note
                            {topic.notes.length !== 1 ? "s" : ""})
                          </span>
                        </div>

                        {topic.summary && (
                          <div className="text-xs text-gray-600 bg-white/50 rounded p-2 mb-2 italic whitespace-pre-wrap">
                            💡 {topic.summary}
                          </div>
                        )}

                        <div className="space-y-1">
                          {topic.notes.map((note) => (
                            <div
                              key={note.id}
                              className="text-xs text-gray-500 flex items-center gap-2"
                            >
                              <span className="text-gray-400 whitespace-nowrap">
                                {formatTime(note.createdAt)}
                              </span>
                              <span className="text-gray-600 truncate">
                                {note.content.substring(0, 50)}
                                {note.content.length > 50 ? "..." : ""}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function formatDateLabel(date: Date): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetDate = new Date(date);
  targetDate.setHours(0, 0, 0, 0);

  const diffTime = today.getTime() - targetDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";

  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  return `${days[date.getDay()]} - ${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}
